import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, ArrowLeft, Check, ChevronRight, Clock3, ExternalLink, RefreshCw, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { API_BASE, apiHeaders } from "../config/api";
import { useXboxDataset } from "../services/xboxDataService";
import { getOperationalStatus, getSnapshotReportingDate, isTelemetryCurrent } from "../utils/xboxAdminStatus";
import "./xboxAdmin.css";

/* Interactive data-flow architecture. Each node carries accurate component,
 * script/service, I/O, timing, dependency, and failure detail. Edges connect
 * node ids in the real direction of data flow. */
const ARCHITECTURE_NODES = [
  {
    id: "friends-api", branch: "discovery", title: "Xbox Friends API", eyebrow: "Microsoft",
    purpose: "Microsoft friends-graph endpoint used as the discovery source.",
    resource: "Microsoft OAuth + Xbox Live Friends API",
    io: "Out: friend XUID edges",
    timing: "Polled during the :15–:54 UTC discovery window",
    depends: "Microsoft OAuth token (AWS Secrets Manager)",
    failure: "Rate limiting (HTTP 429) slows discovery; no production data is lost.",
  },
  {
    id: "discovery-worker", branch: "discovery", title: "XUID Discovery Worker", eyebrow: "EC2 · service",
    purpose: "Crawls the friends graph to enumerate unique Xbox profile XUIDs.",
    resource: "mindgame-xbox-discovery.service · discovery/xbox_discovery_worker.py",
    io: "In: friend edges · Out: new unique XUIDs",
    timing: "Hourly :15–:54 UTC · 1.5s pacing · 65s 429 cooldown",
    depends: "Xbox Friends API, SQLite registry",
    failure: "Discovery stalls; Presence panel keeps using the last exported registry.",
  },
  {
    id: "sqlite-registry", branch: "discovery", title: "SQLite Registry", eyebrow: "EC2 · state",
    purpose: "Deduplicated registry of every discovered XUID and its crawl state.",
    resource: "discovery/xbox_xuids.sqlite",
    io: "In: new XUIDs · Out: hourly panel + crawl backlog",
    timing: "Incremental commits throughout discovery",
    depends: "Discovery worker",
    failure: "Discovery cannot persist progress; panel export has no fresh input.",
  },
  {
    id: "panel-export", branch: "presence", title: "Hourly Panel Export", eyebrow: "EC2 · :00 UTC",
    purpose: "Builds the fixed XUID panel scanned by the hourly Presence run.",
    resource: "panel_export.py · start of xbox_hourly_runner.py",
    io: "In: SQLite registry · Out: scan panel",
    timing: "Runs at the start of each :00 UTC Presence run",
    depends: "SQLite registry",
    failure: "Presence scans a stale panel; new XUIDs are not observed.",
  },
  {
    id: "presence-collector", branch: "presence", title: "Presence Collector", eyebrow: "EC2 · service",
    purpose: "Observes foreground activity for every XUID in the hourly panel.",
    resource: "mindgame-xbox-presence.service · test/xbox_collector_aws.py",
    io: "In: scan panel · Out: raw presence observations",
    timing: "Hourly :00 UTC · typically finishes :07–:12",
    depends: "Panel export, Xbox presence endpoint",
    failure: "No new observations for the hour; dashboard snapshot is not refreshed.",
  },
  {
    id: "validation", branch: "presence", title: "Validation", eyebrow: "EC2",
    purpose: "Validates and archives each run before masters are updated.",
    resource: "xbox_hourly_runner.py · archive/ + logs/",
    io: "In: raw observations · Out: validated run",
    timing: "Within the hourly Presence run",
    depends: "Presence collector",
    failure: "Run is archived but not published; masters stay on the prior hour.",
  },
  {
    id: "masters", branch: "presence", title: "Historical + Metadata Masters", eyebrow: "EC2 · state",
    purpose: "Long-lived master history and title metadata accumulated per run.",
    resource: "xbox_snapshot_history_master.csv · xbox_title_metadata_master.json",
    io: "In: validated run · Out: updated masters",
    timing: "Updated after each successful Presence run",
    depends: "Validation",
    failure: "History stops growing; dashboard keeps serving the last published snapshot.",
  },
  {
    id: "prod-s3", branch: "publication", title: "Production S3", eyebrow: "AWS",
    purpose: "Published production datasets for the dashboard pipeline.",
    resource: "s3://mindgamedataapi/Xbox/xbox_current_snapshot.csv (+ history, metadata)",
    io: "In: published run · Out: snapshot source objects",
    timing: "Published at the end of each successful Presence run",
    depends: "Historical + metadata masters",
    failure: "Dashboard data goes stale; client dashboard keeps serving prior objects.",
  },
  {
    id: "snapshot-lambda", branch: "publication", title: "Snapshot Lambda", eyebrow: "AWS",
    purpose: "Builds the optimized, revisioned dashboard datasets and manifest.",
    resource: "Lambda mindgame-build-xbox-snapshot",
    io: "In: production S3 snapshot · Out: optimized manifest + revisioned JSON",
    timing: "On snapshot publication",
    depends: "Production S3",
    failure: "No new dashboard revision; clients keep the previous manifest.",
  },
  {
    id: "dashboard-manifest", branch: "publication", title: "Optimized Manifest", eyebrow: "AWS · S3",
    purpose: "Revisioned manifest the client dashboard reads to load current data.",
    resource: "s3://mindgamedataapi/Xbox/dashboard/manifest.json",
    io: "In: Lambda build · Out: current + per-title revisioned JSON",
    timing: "Published by the snapshot Lambda",
    depends: "Snapshot Lambda",
    failure: "Client dashboard cannot resolve a new snapshot revision.",
  },
  {
    id: "xbox-dashboard", branch: "publication", title: "Xbox Dashboard", eyebrow: "Client",
    purpose: "The client-facing /xbox dashboard users see.",
    resource: "frontend /xbox · xboxDataService",
    io: "In: optimized manifest + revisioned JSON",
    timing: "On user load",
    depends: "Optimized manifest",
    failure: "Users see the last available snapshot; no admin data is exposed.",
  },
  {
    id: "ec2-metrics", branch: "telemetry", title: "EC2 Operational Metrics", eyebrow: "EC2",
    purpose: "Aggregate service, timer, and run counters from the collector host.",
    resource: "systemd services/timers · latest_run.json · latest_success.json",
    io: "Out: sanitized aggregate counters",
    timing: "Sampled every five minutes",
    depends: "systemd, collector run state",
    failure: "Admin console shows Unknown; collectors are unaffected.",
  },
  {
    id: "telemetry-publisher", branch: "telemetry", title: "Telemetry Publisher", eyebrow: "EC2 · timer",
    purpose: "Publishes sanitized operational telemetry for this admin console.",
    resource: "admin/xbox_admin_telemetry.py · systemd timer",
    io: "In: EC2 metrics · Out: bounded telemetry JSON",
    timing: "Every five minutes",
    depends: "EC2 operational metrics",
    failure: "Telemetry goes stale; admin metrics show Unknown. Monitoring only.",
  },
  {
    id: "telemetry-s3", branch: "telemetry", title: "Private S3 Telemetry", eyebrow: "AWS · private",
    purpose: "Private telemetry object read only by the admin backend.",
    resource: "s3://mindgamedataapi/Xbox/admin/telemetry/latest.json",
    io: "In: telemetry publisher · Out: admin API read",
    timing: "Overwritten every five minutes",
    depends: "Telemetry publisher",
    failure: "Admin API reports telemetry unavailable; no raw XUIDs are stored here.",
  },
  {
    id: "admin-api", branch: "telemetry", title: "Admin API", eyebrow: "FastAPI",
    purpose: "Admin-authorized, read-only endpoint that validates and serves telemetry.",
    resource: "GET /api/admin/xbox/overview · xbox_admin.py",
    io: "In: private telemetry + manifest metadata · Out: sanitized overview",
    timing: "On admin request",
    depends: "Private S3 telemetry, admin authorization",
    failure: "Admin console cannot load metrics; client dashboard is unaffected.",
  },
  {
    id: "xbox-admin", branch: "telemetry", title: "Xbox Admin", eyebrow: "This page",
    purpose: "This read-only operations console.",
    resource: "frontend /admin/xbox · XboxAdmin.jsx",
    io: "In: admin API overview + dashboard snapshot",
    timing: "On admin load / refresh",
    depends: "Admin API, dashboard snapshot",
    failure: "No operational visibility; no production component is changed.",
  },
];

const ARCHITECTURE_EDGES = [
  ["friends-api", "discovery-worker"],
  ["discovery-worker", "sqlite-registry"],
  ["sqlite-registry", "panel-export"],
  ["panel-export", "presence-collector"],
  ["presence-collector", "validation"],
  ["validation", "masters"],
  ["masters", "prod-s3"],
  ["prod-s3", "snapshot-lambda"],
  ["snapshot-lambda", "dashboard-manifest"],
  ["dashboard-manifest", "xbox-dashboard"],
  ["ec2-metrics", "telemetry-publisher"],
  ["telemetry-publisher", "telemetry-s3"],
  ["telemetry-s3", "admin-api"],
  ["admin-api", "xbox-admin"],
];

const ARCHITECTURE_BRANCHES = [
  { id: "discovery", label: "Discovery", caption: "XUID enumeration · :15–:54 UTC" },
  { id: "presence", label: "Presence", caption: "Hourly panel observation · :00 UTC" },
  { id: "publication", label: "Client publication", caption: "Optimized dashboard snapshot" },
  { id: "telemetry", label: "Admin telemetry", caption: "Separate monitoring path · every 5 min" },
];

function formatNumber(value) {
  return Number.isInteger(value) ? value.toLocaleString() : "Unknown";
}

function formatDate(value) {
  if (!value || !Number.isFinite(Date.parse(value))) return "Unknown";
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }) + " UTC";
}

function statusClass(status) {
  return `xoa-status xoa-status-${status.tone}`;
}

function Stat({ label, value, note }) {
  return <article className="xoa-stat">
    <span>{label}</span>
    <strong>{value}</strong>
    {note && <small>{note}</small>}
  </article>;
}

function StatusPill({ status }) {
  return <span className={statusClass(status)}><i aria-hidden="true" />{status.label}</span>;
}

function Control({ children }) {
  return <button type="button" className="xoa-disabled-control" disabled title="Operations controls are not enabled in this console">
    {children}<span>Not available</span>
  </button>;
}

export default function XboxAdmin() {
  const { dataset, loading: snapshotLoading, error: snapshotError, retry: retrySnapshot } = useXboxDataset();
  const [requestNumber, setRequestNumber] = useState(0);
  const [overview, setOverview] = useState({ status: "loading" });
  const [selectedNode, setSelectedNode] = useState("presence-collector");

  const loadOverview = useCallback(async (signal) => {
    setOverview({ status: "loading" });
    try {
      const response = await fetch(`${API_BASE}/admin/xbox/overview`, {
        headers: apiHeaders(),
        cache: "no-store",
        signal,
      });
      if (!response.ok) {
        let message = `Operations request failed with HTTP ${response.status}`;
        if (response.status === 503) {
          const errorPayload = await response.json();
          if (errorPayload?.detail?.code === "snapshot_manifest_access_denied") {
            message = "Backend cannot read the Xbox snapshot manifest. No AWS permissions were changed.";
          } else if (errorPayload?.detail?.code === "snapshot_manifest_unavailable") {
            message = "Backend could not read Xbox snapshot metadata. Check backend S3 access and service health.";
          }
        }
        throw new Error(message);
      }
      const payload = await response.json();
      if (!payload || !Number.isFinite(Date.parse(payload.published_at)) ||
          !Number.isInteger(payload.manifest_size_bytes) ||
          !["available", "not_published", "access_denied", "invalid", "unavailable"].includes(payload.telemetry_status) ||
          (payload.telemetry_status === "available" ? !payload.telemetry : payload.telemetry != null)) {
        throw new Error("The operations API returned an invalid response.");
      }
      setOverview({ status: "ready", payload });
    } catch (error) {
      if (error.name !== "AbortError") {
        setOverview({ status: "error", message: error.message || "Unable to load Xbox operations." });
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadOverview(controller.signal);
    return () => controller.abort();
  }, [loadOverview, requestNumber]);

  const payload = overview.status === "ready" ? overview.payload : null;
  const telemetry = payload?.telemetry_status === "available" ? payload.telemetry : null;
  const snapshotAsOf = dataset?.compact?.asOf || null;
  const snapshotReportingDate = getSnapshotReportingDate(snapshotAsOf);
  const snapshotTitleCount = dataset?.compact?.rows?.all?.length;
  const telemetryCountMismatch = telemetry?.latest_snapshot_title_count != null &&
    snapshotTitleCount != null && telemetry.latest_snapshot_title_count !== snapshotTitleCount;
  const telemetryCurrent = isTelemetryCurrent(telemetry, snapshotAsOf) && !telemetryCountMismatch;
  const currentTelemetry = telemetryCurrent ? telemetry : null;
  const selectedArchNode = ARCHITECTURE_NODES.find((node) => node.id === selectedNode) || ARCHITECTURE_NODES[0];
  const presenceStatus = useMemo(() => getOperationalStatus(currentTelemetry, "presence"), [currentTelemetry]);
  const discoveryStatus = useMemo(() => getOperationalStatus(currentTelemetry, "discovery"), [currentTelemetry]);
  const instanceStatus = useMemo(() => {
    if (!currentTelemetry) return { label: "Unknown", tone: "unknown" };
    const states = {
      running: { label: "Running", tone: "live" },
      pending: { label: "Starting", tone: "warning" },
      stopping: { label: "Stopping", tone: "warning" },
      stopped: { label: "Stopped", tone: "paused" },
      terminated: { label: "Terminated", tone: "error" },
      unknown: { label: "Unknown", tone: "unknown" },
    };
    return states[currentTelemetry.instance_state] || states.unknown;
  }, [currentTelemetry]);

  const latestPresenceRun = currentTelemetry?.presence?.latest_run;
  const discovery = currentTelemetry?.discovery;
  const snapshotVersionMessage = payload?.telemetry_status === "available" && snapshotAsOf && telemetry &&
    getSnapshotReportingDate(telemetry.snapshot_as_of) !== snapshotReportingDate
    ? `Telemetry is for ${telemetry.snapshot_as_of}; the dashboard snapshot is ${snapshotReportingDate || "unknown"}. Operational metrics are withheld to prevent mixing reporting weeks.`
    : telemetryCountMismatch
      ? "Telemetry title counts do not match the current dashboard snapshot. Operational metrics are withheld."
      : null;

  const refresh = () => {
    setRequestNumber((number) => number + 1);
    retrySnapshot();
  };

  return <main className="xoa-page">
    <header className="xoa-header">
      <div className="xoa-brand">
        <Link to="/xbox" className="xoa-back" aria-label="Return to Xbox dashboard"><ArrowLeft size={16} aria-hidden="true" /></Link>
        <div>
          <div className="xoa-kicker"><ShieldCheck size={13} aria-hidden="true" /> ADMIN OPERATIONS</div>
          <h1>Xbox <span>Operations</span></h1>
          <p>Read-only visibility into collector health, discovery, and dashboard publication.</p>
        </div>
      </div>
      <div className="xoa-header-actions">
        <button type="button" className="xoa-refresh" onClick={refresh} disabled={overview.status === "loading" || snapshotLoading}>
          <RefreshCw size={15} aria-hidden="true" className={overview.status === "loading" || snapshotLoading ? "xoa-spin" : ""} />
          Refresh
        </button>
      </div>
    </header>

    {overview.status === "error" && <div className="xoa-alert" role="alert"><AlertTriangle size={17} aria-hidden="true" />{overview.message}</div>}
    {snapshotError && <div className="xoa-alert" role="alert"><AlertTriangle size={17} aria-hidden="true" />Dashboard snapshot unavailable: {snapshotError}</div>}
    {snapshotVersionMessage && <div className="xoa-alert" role="alert"><AlertTriangle size={17} aria-hidden="true" />{snapshotVersionMessage}</div>}
    {payload?.telemetry_status === "not_published" && <div className="xoa-notice" role="status">
      <Activity size={17} aria-hidden="true" /><span>No operational telemetry has been published. Collector and discovery metrics remain Unknown; the production collectors are not modified by this page.</span>
    </div>}
    {payload?.telemetry_status === "access_denied" && <div className="xoa-alert" role="alert">
      <AlertTriangle size={17} aria-hidden="true" /><span>S3 denied the read for <code>Xbox/admin/telemetry/latest.json</code>. The object may be absent or backend access may be missing; S3 can hide a missing object as AccessDenied. No AWS permissions were changed.</span>
    </div>}
    {payload?.telemetry_status === "invalid" && <div className="xoa-alert" role="alert"><AlertTriangle size={17} aria-hidden="true" />The telemetry object failed the versioned schema checks. Live collector metrics are withheld.</div>}
    {payload?.telemetry_status === "unavailable" && <div className="xoa-alert" role="alert"><AlertTriangle size={17} aria-hidden="true" />The telemetry object could not be read. Live collector metrics are withheld.</div>}

    <section className="xoa-overview" aria-labelledby="xoa-overview-title">
      <div className="xoa-section-heading">
        <div><span className="xoa-section-index">01</span><h2 id="xoa-overview-title">System overview</h2></div>
        <span className="xoa-readonly"><Check size={13} aria-hidden="true" /> READ ONLY</span>
      </div>
      <div className="xoa-overview-grid">
        <article className="xoa-feature-card">
          <div className="xoa-card-top"><span>EC2 Host</span><StatusPill status={instanceStatus} /></div>
          <strong>{currentTelemetry?.instance_type || "Xbox collector instance"}</strong>
          <p>{currentTelemetry ? `Reported state: ${currentTelemetry.instance_state}` : "Host state is Unknown until current, matching telemetry is published."}</p>
        </article>
        <article className="xoa-feature-card">
          <div className="xoa-card-top"><span>Presence Panel</span><StatusPill status={presenceStatus} /></div>
          <strong>{formatNumber(latestPresenceRun?.panel_size)} <small>XUIDs scanned</small></strong>
          <p>{latestPresenceRun ? `Latest run ${latestPresenceRun.status} · ${formatDate(latestPresenceRun.started_at)}.` : "No verified run details are available."}</p>
        </article>
        <article className="xoa-feature-card">
          <div className="xoa-card-top"><span>Discovery Registry</span><StatusPill status={discoveryStatus} /></div>
          <strong>{formatNumber(discovery?.unique_xuids)} <small>unique XUIDs</small></strong>
          <p>{discovery ? `${formatNumber(discovery.crawled_xuids)} graph-crawled · ${formatNumber(discovery.uncrawled_xuids)} pending.` : "Registry counts are Unknown until current telemetry is available."}</p>
          {discovery && discovery.target > 0 && (
            <div className="xoa-progress" role="progressbar" aria-valuemin={0} aria-valuemax={discovery.target} aria-valuenow={discovery.unique_xuids} aria-label="Progress toward the discovery target">
              <div className="xoa-progress-track"><div className="xoa-progress-fill" style={{ width: `${Math.min(100, (discovery.unique_xuids / discovery.target) * 100)}%` }} /></div>
              <div className="xoa-progress-meta"><span>{((discovery.unique_xuids / discovery.target) * 100).toFixed(1)}% of target</span><span>{formatNumber(discovery.target)} target</span></div>
            </div>
          )}
        </article>
        <article className="xoa-feature-card">
          <div className="xoa-card-top"><span>Dashboard Catalog</span><span className={`xoa-status xoa-status-${snapshotError || snapshotLoading ? "unknown" : "healthy"}`}><i aria-hidden="true" />{snapshotLoading ? "Loading" : snapshotError ? "Unavailable" : "Available"}</span></div>
          <strong>{formatNumber(snapshotTitleCount)} <small>current titles</small></strong>
          <p>{snapshotAsOf ? `Snapshot as of ${snapshotAsOf}.` : "Snapshot reporting period is Unknown."}</p>
        </article>
      </div>
      <div className="xoa-publication-line">
        <Clock3 size={15} aria-hidden="true" />
        <span>Dashboard manifest last modified</span>
        <strong>{payload ? formatDate(payload.published_at) : "Unknown"}</strong>
        <span className="xoa-separator">·</span>
        <span>Manifest size</span>
        <strong>{payload ? `${formatNumber(payload.manifest_size_bytes)} bytes` : "Unknown"}</strong>
        {payload?.manifest_etag && <code title="Manifest ETag">{payload.manifest_etag}</code>}
      </div>
    </section>

    <section className="xoa-grid-section" aria-label="Collector details">
      <article className="xoa-panel" id="presence">
        <div className="xoa-section-heading">
          <div><span className="xoa-section-index">02</span><h2>Presence</h2></div>
          <StatusPill status={presenceStatus} />
        </div>
        <p className="xoa-section-copy">Scheduled panel observation and snapshot publication. The displayed time is UTC.</p>
        <div className="xoa-stats-grid">
          <Stat label="Panel size" value={formatNumber(latestPresenceRun?.panel_size)} />
          <Stat label="Foreground observations" value={formatNumber(latestPresenceRun?.foreground_observations)} />
          <Stat label="Tracked titles" value={formatNumber(latestPresenceRun?.tracked_titles)} />
          <Stat label="Run duration" value={latestPresenceRun?.duration_seconds == null ? "Unknown" : `${formatNumber(latestPresenceRun.duration_seconds)} sec`} />
        </div>
        <dl className="xoa-detail-list">
          <div><dt>Last successful run</dt><dd>{formatDate(currentTelemetry?.presence?.last_success_at)}</dd></div>
          <div><dt>Latest snapshot timestamp</dt><dd>{formatDate(latestPresenceRun?.snapshot_at)}</dd></div>
          <div><dt>Latest publication result</dt><dd>{latestPresenceRun?.publication_status || "Unknown"}</dd></div>
          <div><dt>Recent error code</dt><dd>{latestPresenceRun?.error_code || "None reported"}</dd></div>
        </dl>
        {currentTelemetry?.presence?.recent_runs?.length > 0 && <details className="xoa-history">
          <summary>Recent presence runs<ChevronRight size={14} aria-hidden="true" /></summary>
          <ul>{currentTelemetry.presence.recent_runs.slice(0, 8).map((run, index) => <li key={`${run.started_at}-${index}`}>
            <span>{formatDate(run.started_at)}</span><strong>{run.status}</strong><span>{formatNumber(run.panel_size)} titles</span><span>{run.error_code || "No error"}</span>
          </li>)}</ul>
        </details>}
      </article>

      <article className="xoa-panel" id="discovery">
        <div className="xoa-section-heading">
          <div><span className="xoa-section-index">03</span><h2>Discovery</h2></div>
          <StatusPill status={discoveryStatus} />
        </div>
        <p className="xoa-section-copy">Unique profile discovery and friends-graph crawl progress. Identifiers and raw logs are never displayed. <strong>Pending graph crawl</strong> means accounts whose friends graphs have not yet been explored — not accounts missing from the Presence panel.</p>
        {discovery && discovery.target > 0 && (
          <div className="xoa-progress xoa-progress-large" role="progressbar" aria-valuemin={0} aria-valuemax={discovery.target} aria-valuenow={discovery.unique_xuids} aria-label="Progress toward the 10,000,000 XUID discovery target">
            <div className="xoa-progress-track"><div className="xoa-progress-fill" style={{ width: `${Math.min(100, (discovery.unique_xuids / discovery.target) * 100)}%` }} /></div>
            <div className="xoa-progress-meta"><span>{((discovery.unique_xuids / discovery.target) * 100).toFixed(1)}% of {formatNumber(discovery.target)} target</span><span>{formatNumber(discovery.target - discovery.unique_xuids)} to go</span></div>
          </div>
        )}
        <div className="xoa-stats-grid">
          <Stat label="Target" value={formatNumber(discovery?.target)} />
          <Stat label="Unique XUIDs" value={formatNumber(discovery?.unique_xuids)} />
          <Stat label="Graph-crawled" value={formatNumber(discovery?.crawled_xuids)} />
          <Stat label="Pending graph crawl" value={formatNumber(discovery?.uncrawled_xuids)} />
          <Stat label="New · last hour" value={formatNumber(discovery?.new_xuids_last_hour)} />
          <Stat label="New · last 24 hours" value={formatNumber(discovery?.new_xuids_last_24h)} />
          <Stat label="Rate-limit events" value={formatNumber(discovery?.rate_limit_events)} />
          <Stat label="Network / server errors" value={discovery ? `${formatNumber(discovery.network_errors)} / ${formatNumber(discovery.server_errors)}` : "Unknown"} />
        </div>
        <dl className="xoa-detail-list">
          <div><dt>Last started</dt><dd>{formatDate(discovery?.last_started_at)}</dd></div>
          <div><dt>Last successful run</dt><dd>{formatDate(discovery?.last_success_at)}</dd></div>
          <div><dt>Next scheduled start</dt><dd>{formatDate(discovery?.next_scheduled_at)}</dd></div>
          <div><dt>Metadata catalog entries</dt><dd>{formatNumber(currentTelemetry?.metadata_catalog_count)}</dd></div>
        </dl>
        {discovery?.recent_events?.length > 0 && <details className="xoa-history">
          <summary>Recent safe event counts<ChevronRight size={14} aria-hidden="true" /></summary>
          <ul>{discovery.recent_events.slice(0, 8).map((event, index) => <li key={`${event.occurred_at}-${index}`}>
            <span>{formatDate(event.occurred_at)}</span><strong>{event.event_type}</strong><span>{formatNumber(event.count)} occurrences</span>
          </li>)}</ul>
        </details>}
      </article>
    </section>

    <section className="xoa-pipeline-section" aria-labelledby="xoa-pipeline-title">
      <div className="xoa-section-heading">
        <div><span className="xoa-section-index">04</span><h2 id="xoa-pipeline-title">Pipeline architecture</h2></div>
        <span className="xoa-caption">Select any component for details</span>
      </div>
      <div className="xoa-arch">
        {ARCHITECTURE_BRANCHES.map((branch) => {
          const nodes = ARCHITECTURE_NODES.filter((node) => node.branch === branch.id);
          return (
            <div className={`xoa-arch-branch xoa-arch-${branch.id}`} key={branch.id}>
              <div className="xoa-arch-branch-head"><strong>{branch.label}</strong><span>{branch.caption}</span></div>
              <div className="xoa-arch-flow">
                {nodes.map((node, index) => (
                  <div className="xoa-arch-item" key={node.id}>
                    <button type="button"
                      className={`xoa-arch-node${selectedNode === node.id ? " is-selected" : ""}`}
                      aria-pressed={selectedNode === node.id}
                      onClick={() => setSelectedNode(node.id)}>
                      <small>{node.eyebrow}</small>
                      <strong>{node.title}</strong>
                    </button>
                    {index < nodes.length - 1 && (
                      <span className={`xoa-arch-arrow${ARCHITECTURE_EDGES.some(([a, b]) => a === node.id && b === nodes[index + 1].id) ? "" : " xoa-arch-arrow-gap"}`} aria-hidden="true"><ChevronRight size={15} /></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <div className="xoa-arch-crosslinks" aria-hidden="true">
          <span title="SQLite Registry feeds the hourly Presence panel">SQLite Registry → Panel Export</span>
          <span title="Admin telemetry is a separate monitoring path and never drives the client dashboard">Telemetry is monitoring-only</span>
        </div>
      </div>
      <div className="xoa-arch-detail" aria-live="polite">
        <div className="xoa-arch-detail-head">
          <span>{selectedArchNode.eyebrow}</span>
          <h3>{selectedArchNode.title}</h3>
          <p>{selectedArchNode.purpose}</p>
        </div>
        <dl className="xoa-arch-detail-grid">
          <div><dt>Component</dt><dd>{selectedArchNode.resource}</dd></div>
          <div><dt>Inputs / outputs</dt><dd>{selectedArchNode.io}</dd></div>
          <div><dt>Timing</dt><dd>{selectedArchNode.timing}</dd></div>
          <div><dt>Depends on</dt><dd>{selectedArchNode.depends}</dd></div>
          <div className="xoa-arch-failure"><dt>If it fails</dt><dd>{selectedArchNode.failure}</dd></div>
        </dl>
      </div>
      <div className="xoa-schedule-row">
        <article><span>Presence starts</span><strong>:00 UTC</strong><small>Scheduled start. Typical finish :07–:12 (observed, variable).</small></article>
        <article><span>Discovery window</span><strong>:15 – :54 UTC</strong><small>Scheduled start :15; pauses before Presence, resumes after.</small></article>
        <article><span>Admin telemetry</span><strong>Every 5 min</strong><small>Publisher refresh cadence; independent of the collectors.</small></article>
      </div>
      <p className="xoa-schedule-note">Scheduled times are configured targets; run durations are observed and variable. Live status is Unknown without fresh, matching telemetry. The admin telemetry branch is a separate monitoring pathway and does not drive the client Xbox dashboard.</p>
    </section>

    <section className="xoa-controls" aria-labelledby="xoa-controls-title">
      <div className="xoa-section-heading">
        <div><span className="xoa-section-index">05</span><h2 id="xoa-controls-title">Operations controls</h2></div>
        <span className="xoa-readonly">DISABLED</span>
      </div>
      <p>This console is strictly read-only. No action here can start, stop, pause, or reconfigure a production collector. These controls are placeholders for future capabilities and remain disabled.</p>
      <div className="xoa-control-row">
        <Control>Increase discovery target</Control><Control>Pause / resume discovery</Control><Control>Request Presence scan</Control><Control>Request metadata refresh</Control>
      </div>
    </section>

    <footer className="xoa-footer">
      <span>Telemetry freshness: {telemetryCurrent ? formatDate(telemetry.generated_at) : "Unknown / mismatched"}</span>
      <span><ExternalLink size={12} aria-hidden="true" /> No production resources were changed</span>
    </footer>
  </main>;
}
